import { PerformanceTracker } from "./performance-tracker.js";
class PerformanceService extends PerformanceTracker {
    constructor(options = {}){
        super({
            enabled: true,
            trackJavaScript: true,
            trackMemory: true,
            trackRendering: true,
            trackBlocking: true,
            collectInterval: 1000,
            enableLogging: false,
            ...options
        });
    }
    onModuleInit() {
        this.start();
    }
    onApplicationShutdown() {
        this.destroy();
    }
    getPerformanceSummary() {
        const metrics = this.getMetrics();
        const report = this.generateReport();
        return {
            score: report.summary.overallScore,
            bottleneck: report.summary.primaryBottleneck,
            metrics: {
                avgResponseTime: metrics.javascript.averageFunctionTime,
                memoryUsage: metrics.memory.currentUsage,
                fps: metrics.rendering.fps,
                blockingTime: metrics.blocking.totalBlockingTime
            },
            issues: report.issues.filter((issue)=>'high' === issue.severity || 'critical' === issue.severity)
        };
    }
    logMetrics() {
        const metrics = this.getMetrics();
        const report = this.generateReport();
        console.group('🚀 Performance Metrics');
        console.log(`Overall Score: ${report.summary.overallScore}/100`);
        console.log(`Primary Bottleneck: ${report.summary.primaryBottleneck}`);
        console.group('JavaScript');
        console.log(`Avg Function Time: ${metrics.javascript.averageFunctionTime.toFixed(2)}ms`);
        console.log(`Total Execution Time: ${metrics.javascript.totalExecutionTime.toFixed(2)}ms`);
        console.log(`Function Calls: ${metrics.javascript.functionCallCount}`);
        console.groupEnd();
        console.group('Memory');
        console.log(`Current Usage: ${(metrics.memory.currentUsage / 1024 / 1024).toFixed(2)}MB`);
        console.log(`Peak Usage: ${(metrics.memory.peakUsage / 1024 / 1024).toFixed(2)}MB`);
        console.log(`Memory Growth: ${(metrics.memory.memoryGrowth / 1024 / 1024).toFixed(2)}MB`);
        console.groupEnd();
        console.group('Rendering');
        console.log(`Current FPS: ${metrics.rendering.fps}`);
        console.log(`Average FPS: ${metrics.rendering.avgFps.toFixed(1)}`);
        console.log(`Frame Count: ${metrics.rendering.frameCount}`);
        console.groupEnd();
        console.group('Blocking');
        console.log(`Total Blocking Time: ${metrics.blocking.totalBlockingTime.toFixed(2)}ms`);
        console.log(`Longest Block: ${metrics.blocking.longestBlockingTime.toFixed(2)}ms`);
        console.log(`Long Tasks: ${metrics.blocking.longTaskCount}`);
        console.groupEnd();
        if (report.issues.length > 0) {
            console.group('⚠️ Issues');
            report.issues.forEach((issue)=>{
                const emoji = {
                    critical: '🔴',
                    high: '🟠',
                    medium: '🟡',
                    low: '🟢'
                }[issue.severity];
                console.log(`${emoji} ${issue.title}: ${issue.description}`);
            });
            console.groupEnd();
        }
        console.groupEnd();
    }
    measureOperation(name, metadata) {
        const id = this.startMeasurement(name, metadata);
        return ()=>this.endMeasurement(id);
    }
    async measureAsync(name, operation, metadata) {
        const id = this.startMeasurement(name, metadata);
        try {
            return await operation();
        } finally{
            this.endMeasurement(id);
        }
    }
    measureSync(name, operation, metadata) {
        const id = this.startMeasurement(name, metadata);
        try {
            return operation();
        } finally{
            this.endMeasurement(id);
        }
    }
    enableAutomaticMonitoring(intervalMs = 30000) {
        setInterval(()=>{
            const report = this.generateReport();
            const criticalIssues = report.issues.filter((issue)=>'critical' === issue.severity);
            if (criticalIssues.length > 0) {
                console.warn('🔴 Critical Performance Issues Detected:');
                criticalIssues.forEach((issue)=>{
                    console.warn(`- ${issue.title}: ${issue.description}`);
                });
            }
            if (report.summary.overallScore < 60) {
                console.warn(`⚡ Performance Score: ${report.summary.overallScore}/100 (Below Threshold)`);
                console.warn(`Primary Issue: ${report.summary.primaryBottleneck}`);
            }
        }, intervalMs);
    }
}
export { PerformanceService };
